<template>
  <div class="page-tenants">
    <div class="page-tenants__content">
      <!-- Phones: its main action, as the "+" of the settings header. -->
      <PageMainAction :label="$t('page.admin-tenants.new-tenant')" @click="newTenantClick" />
      <!-- Phones: the lists' search and filters panel. -->
      <div v-if="isPhone" class="page-tenants__phone-panel">
        <PhoneFilterBar :active-count="statusValue ? 1 : 0" @clear-all="statusValue = ''">
          <template #search>
            <BaseSearchBarNew
              name="search"
              is-grey
              :value="search"
              :placeholder="$t('page.admin-tenants.find-tenant')"
              @input="search = $event"
            />
          </template>
          <template #filters>
            <FilterSingleChip
              is-text
              icon="tag"
              :label="$t('search-filters.status.name')"
              :items="statusItems"
              :value="statusValue || null"
              @change="onStatusFilterChange"
            />
          </template>
        </PhoneFilterBar>
      </div>
      <!-- Summary cards double as filters. -->
      <div class="page-tenants__summary">
        <button
          v-for="card in summaryCards"
          :key="card.key || 'all'"
          class="page-tenants__summary-card"
          :class="{ 'is-active': activeFilter === card.key, [`tone-${card.tone}`]: true }"
          type="button"
          :aria-pressed="String(activeFilter === card.key)"
          @click="toggleFilter(card.key)"
        >
          <span class="page-tenants__summary-value number-monospace">{{ card.value }}</span>
          <span class="page-tenants__summary-label">{{ card.label }}</span>
        </button>
      </div>

      <!-- Imported Stripe customers with no tenant yet. -->
      <UnlinkedCustomersPanel />

      <div v-if="!isPhone" class="page-tenants__search-area">
        <BaseSearchBar
          class="page-tenants__search-bar"
          v-model="search"
          name="search"
          type="text"
          :placeholder="$t('page.admin-tenants.find-tenant')"
        ></BaseSearchBar>
        <div class="page-tenants__search-area--filter-item">
          <FilterSingleChip
            is-text
            label-mode="collapse"
            icon="tag"
            :label="$t('search-filters.status.name')"
            :items="statusItems"
            :value="statusValue || null"
            @change="onStatusFilterChange"
          />
        </div>
        <div class="page-admin__actions">
          <BaseButton has-icon-left @click.native="newTenantClick">
            <BaseIcon name="plus" />
            {{ $t('page.admin-tenants.new-tenant') }}
          </BaseButton>
          <BaseRoundButton
            icon="download-cloud"
            :size="40"
            is-white
            is-floating
            :is-loading="syncing"
            :tooltip-text="$t('page.admin-subscriptions.sync')"
            @click.native="doSync"
          />
          <BaseRefreshButton :size="40" :pending="refreshing" :disabled="syncing" @refresh="onRefreshClick" />
        </div>
      </div>

      <BaseInfoBanner
        v-if="billingUnavailableCount && !pending"
        class="page-tenants__billing-unavailable"
        tone="warning"
        preset="soft"
        :title="$t('page.admin-subscriptions.billing-unavailable')"
      >
        <template #icon>
          <BaseIcon name="alert-triangle" />
        </template>
      </BaseInfoBanner>

      <div v-if="!isPhone" class="tenders-data-table__count">
        <span class="number-monospace">{{ filteredTenants.length }}</span>
        {{ ' ' + $tc('page.admin-tenants.count-of', filteredTenants.length, { total: baseTenants.length }) }}
      </div>

      <div class="page-tenants__table">
        <TableNormal
          :headers="headers"
          :items="filteredTenants"
          :items-per-page="-1"
          class="elevation-1 page-tenants__data-table"
          :sort-by="sortBy"
          :sort-desc="sortDesc"
          :loading="pending"
          :loading-text="$t('page.admin-tenants.loading')"
          :no-data-text="$t('page.admin-tenants.no-tenants')"
          hide-default-footer
          item-key="tenantId"
          :expanded.sync="expanded"
          @click:row="onRowClick"
        >
          <template v-slot:[`item.tenantName`]="{ item }">
            <span :class="{ 'page-tenants__inactive': !item.tenantActive }">
              {{ item.tenantName }}
            </span>
          </template>

          <template v-slot:[`item.linked`]="{ item }">
            <span v-if="item.billingUnavailable" v-show="!isPhone" class="page-tenants__muted">—</span>
            <BaseTag v-else-if="item.linked" :style="getSubscriptionStatusStyle('active')">
              {{ $t('page.admin-subscriptions.linked') }}
            </BaseTag>
            <BaseTag v-else :style="getSubscriptionStatusStyle('canceled')">
              {{ $t('page.admin-subscriptions.not-linked') }}
            </BaseTag>
          </template>

          <!-- Plan code when tagged, else the Stripe nickname, else "untagged". -->
          <template v-slot:[`item.planCode`]="{ item }">
            <span v-if="item.planCode">
              {{ planLabel(item.planCode) }}
              <span class="page-tenants__interval">{{ intervalLabel(item.planInterval) }}</span>
            </span>
            <span
              v-else-if="item.priceNickname"
              class="page-tenants__untagged"
              v-tooltip="$t('page.admin-subscriptions.untagged-price')"
            >
              {{ item.priceNickname }}
            </span>
            <span
              v-else-if="item.unitAmount != null"
              class="page-tenants__untagged"
              v-tooltip="$t('page.admin-subscriptions.untagged-price')"
            >
              {{ $t('page.admin-subscriptions.unlinked-panel.untagged') }}
              <span class="page-tenants__interval">{{ intervalLabel(item.planInterval) }}</span>
            </span>
            <span v-else v-show="!isPhone" class="page-tenants__muted">—</span>
          </template>

          <template v-slot:[`item.subscriptionType`]="{ item }">
            <BaseTag v-if="item.subscriptionType" :style="getSubscriptionTagStyle(legacyType(item))">
              {{ legacyLabel(item) }}
            </BaseTag>
            <span v-else class="page-tenants__muted">—</span>
          </template>

          <template v-slot:[`item.subscriptionStatus`]="{ item }">
            <BaseTag v-if="item.subscriptionStatus" :style="getSubscriptionStatusStyle(item.subscriptionStatus)">
              {{ $t(`page.admin-subscriptions.status.${item.subscriptionStatus}`) }}
            </BaseTag>
            <span v-else v-show="!isPhone" class="page-tenants__muted">—</span>
          </template>

          <template v-slot:[`item.biState`]="{ item }">
            <span v-if="item.billingUnavailable" class="page-tenants__muted">—</span>
            <BaseTag v-else :style="getBiStateStyle(item.biState)">
              {{ $t(`page.admin-subscriptions.bi.${item.biState}`) }}
            </BaseTag>
          </template>

          <!-- Trial state, plus days left while it runs. -->
          <template v-slot:[`item.trialState`]="{ item }">
            <span v-if="item.billingUnavailable" v-show="!isPhone" class="page-tenants__muted">—</span>
            <BaseTag v-else :style="getTrialStateStyle(item.trialState)">
              {{ $t(`page.admin-subscriptions.trial.${item.trialState}`) }}
            </BaseTag>
            <span v-if="trialCountdown(item)" class="page-tenants__countdown" :class="trialCountdown(item).tone">
              ({{ trialCountdown(item).text }})
            </span>
            <BaseIcon
              v-if="item.trialDatesDisagree"
              name="alert-triangle"
              class="page-tenants__drift"
              v-tooltip="$t('page.admin-subscriptions.trial-drift')"
            />
          </template>

          <template v-slot:[`item.cardExpiry`]="{ item }">
            <BaseTag v-if="item.cardExpiry" :style="getCardExpiryStyle(item.cardExpiryTone)">
              {{ item.cardExpiry }}
            </BaseTag>
            <span v-else class="page-tenants__muted">—</span>
          </template>

          <template v-slot:[`item.monthlyAmount`]="{ item }">
            <!-- Greyed when nothing bills any more: the last price, kept for reference. -->
            <span
              v-if="item.monthlyAmount != null"
              class="number-monospace"
              :class="{ 'page-tenants__muted': item.subscriptionBilling === false }"
              v-tooltip="item.subscriptionBilling === false ? $t('page.admin-subscriptions.last-price') : null"
            >
              {{ formatAmount(item.monthlyAmount) }}
            </span>
            <span v-else class="page-tenants__muted">—</span>
          </template>

          <!-- Keep the toggle click from expanding the row. -->
          <template v-slot:[`item.tenantActive`]="{ item }">
            <span class="page-tenants__active" @click.stop>
              <BaseToggle :value="item.tenantActive" @change="askToggleActive(item)" />
            </span>
          </template>

          <template v-slot:[`item.data-table-expand`]="{ item }">
            <BaseRoundButton
              class="page-tenants__expand-btn"
              :icon="isExpanded(item) ? 'chevron-up' : 'chevron-down'"
              :size="32"
              is-transparent
              is-neutral
              @click.native.stop="toggleExpand(item)"
            />
          </template>

          <template v-if="!isPhone" v-slot:expanded-item="{ headers, item }">
            <td :colspan="headers.length" class="page-tenants__expanded-cell">
              <TenantBillingDetails
                :item="item"
                :emails="externalEmailsForTenant(item)"
                :busy="busyRows[item.tenantId] || null"
                :countdown="trialCountdown(item)"
                @copy-emails="copyTenantEmails(item)"
                @link="openLinkDialog(item)"
                @resync="doResync(item)"
                @unlink="doUnlink(item)"
                @payment-link="openPaymentLinkDialog(item)"
              />
            </td>
          </template>

          <!-- Phones: the table draws the count line above its rows (docs/page-layout.md). -->
          <template #phone-count>
            <span class="number-monospace">{{ filteredTenants.length }}</span>
            {{ ' ' + $tc('page.admin-tenants.count-of', filteredTenants.length, { total: baseTenants.length }) }}
          </template>
          <template #phone-actions>
            <BaseRoundButton
              icon="download-cloud"
              :size="36"
              is-white
              is-floating
              :is-loading="syncing"
              :aria-label="$t('page.admin-subscriptions.sync')"
              @click.native="doSync"
            />
            <BaseRefreshButton :pending="refreshing" :disabled="syncing" @refresh="onRefreshClick" />
          </template>
        </TableNormal>
      </div>
    </div>

    <!-- Phones: an account's details, from its line. -->
    <BaseBottomSheet v-model="detailsOpen" :title="detailsTenant ? detailsTenant.tenantName : ''">
      <template v-if="detailsTenant">
        <div class="page-tenants__sheet-active">
          <span>{{ $t('page.admin-tenants.data.active') }}</span>
          <BaseToggle :value="detailsTenant.tenantActive" @change="askToggleActive(detailsTenant)" />
        </div>
        <TenantBillingDetails
          :item="detailsTenant"
          :emails="externalEmailsForTenant(detailsTenant)"
          :busy="busyRows[detailsTenant.tenantId] || null"
          :countdown="trialCountdown(detailsTenant)"
          @copy-emails="copyTenantEmails(detailsTenant)"
          @link="fromSheet(openLinkDialog)"
          @resync="doResync(detailsTenant)"
          @unlink="fromSheet(doUnlink)"
          @payment-link="fromSheet(openPaymentLinkDialog)"
        />
      </template>
    </BaseBottomSheet>

    <AddTenantDialog ref="newTenantDialog" @action-succeeded="refresh" />
    <EditTenantDialog ref="updateTenantDialog" @action-succeeded="refresh" />
    <LinkStripeCustomerDialog ref="linkDialog" @action-succeeded="refresh" />
    <PaymentLinkDialog ref="paymentLinkDialog" />
    <ConfirmationDialog
      ref="unlinkDialog"
      is-danger
      :buttonText="$t('page.admin-subscriptions.actions.unlink')"
      @execute="executeUnlink"
    >
      <template slot="content">
        <div v-if="unlinkTarget" class="confirmation-dialog__content">
          {{ $t('page.admin-subscriptions.confirm-unlink', { name: unlinkTarget.tenantName }) }}
        </div>
      </template>
    </ConfirmationDialog>
  </div>
</template>

<script>
import AddTenantDialog from '@/components/page-admin-tenants/AddTenantDialog';
import BaseBottomSheet from '@/components/base/BaseBottomSheet.vue';
import BaseButton from '@/components/base/BaseButton';
import BaseIcon from '@/components/base/BaseIcon';
import BaseInfoBanner from '@/components/base/BaseInfoBanner';
import BaseRefreshButton from '@/components/base/BaseRefreshButton.vue';
import BaseRoundButton from '@/components/base/BaseRoundButton.vue';
import BaseSearchBar from '@/components/base/BaseSearchBar';
import BaseSearchBarNew from '@/components/base/BaseSearchBarNew';
import BaseTag from '@/components/base/BaseTag.vue';
import BaseToggle from '@/components/base/BaseToggle';
import ConfirmationDialog from '@/components/page-admin-invites/ConfirmationDialog';
import DateMixin from '@/mixins/date-mixin';
import EditTenantDialog from '@/components/page-admin-tenants/EditTenantDialog';
import FilterSingleChip from '@/components/filters/FilterSingleChip';
import PhoneFilterBar from '@/components/filters/PhoneFilterBar.vue';
import PageMainAction from '@/components/layout/PageMainAction.vue';
import LinkStripeCustomerDialog from '@/components/page-admin-subscriptions/LinkStripeCustomerDialog.vue';
import PaymentLinkDialog from '@/components/page-admin-subscriptions/PaymentLinkDialog.vue';
import UnlinkedCustomersPanel from '@/components/page-admin-subscriptions/UnlinkedCustomersPanel.vue';
import TenantBillingDetails from '@/components/page-admin-tenants/TenantBillingDetails.vue';
import TableNormal from '@/components/table/TableNormal.vue';

import { copyTextToClipboard } from '@/util/clipboard';
import { getDaysBeforeClosing, getDhm } from '@/util/date-util';
import { getSubscriptionTagStyle } from '@/util/tag-tones/subscription-tag';
import {
  getBiStateStyle,
  getCardExpiryStyle,
  getSubscriptionStatusStyle,
  getTrialStateStyle,
} from '@/util/tag-tones/billing-status-tag';
import { TENANT_STATUS, TENANT_STATUS_DEFAULT, TENANT_STATUS_VALUES } from '@/models/tenants';
import { mapActions, mapGetters } from 'vuex';
import { viewport } from '@/util/viewport';

/**
 * Every tenant with its lifecycle and billing on one row — the former Comptes and Abonnements pages.
 * Rows come from the billing view; the users (for the email list) and the create/activate actions
 * come from the tenants store, which also fills in any tenant the billing view did not return. The
 * legacy subscription type keeps a column beside the Stripe plan until the entitlement resolver
 * replaces it.
 */
export default {
  name: 'PageAdminTenants',
  mixins: [DateMixin],
  components: {
    TableNormal,
    AddTenantDialog,
    BaseBottomSheet,
    BaseButton,
    BaseIcon,
    BaseInfoBanner,
    BaseRefreshButton,
    BaseRoundButton,
    BaseSearchBar,
    BaseSearchBarNew,
    BaseTag,
    BaseToggle,
    ConfirmationDialog,
    EditTenantDialog,
    FilterSingleChip,
    LinkStripeCustomerDialog,
    PaymentLinkDialog,
    PhoneFilterBar,
    PageMainAction,
    TenantBillingDetails,
    UnlinkedCustomersPanel,
  },
  data() {
    return {
      search: '',
      sortBy: ['tenantName'],
      sortDesc: [false],
      expanded: [],
      activeFilter: null,
      // Active by default; the chip also offers inactive and all.
      statusValue: TENANT_STATUS_DEFAULT,
      syncing: false,
      // Own flag: the store's `pending` is raised by every billing action.
      refreshing: false,
      // tenantId -> 'resync' | 'unlink' while that row's Stripe action runs.
      busyRows: {},
      unlinkTarget: null,
      // Phones: the account whose sheet is open.
      detailsId: null,

      headers: [
        { text: this.$t('page.admin-tenants.data.name'), value: 'tenantName', class: 'fixed-size-m', phone: 'title' },
        { text: this.$t('page.admin-subscriptions.data.stripe'), value: 'linked', filterable: false, phone: 'meta' },
        {
          text: this.$t('page.admin-subscriptions.data.plan'),
          value: 'planCode',
          filterable: false,
          phone: 'subtitle',
        },
        // Beside the Stripe plan, so a mismatch is visible at a glance.
        { text: this.$t('page.admin-subscriptions.detail.legacy-type'), value: 'subscriptionType', filterable: false },
        {
          text: this.$t('page.admin-subscriptions.data.status'),
          value: 'subscriptionStatus',
          filterable: false,
          phone: 'meta',
        },
        { text: this.$t('page.admin-subscriptions.data.bi'), value: 'biState', filterable: false },
        { text: this.$t('page.admin-subscriptions.data.trial'), value: 'trialState', filterable: false, phone: 'meta' },
        { text: this.$t('page.admin-subscriptions.data.card'), value: 'cardExpiry', filterable: false },
        {
          text: this.$t('page.admin-subscriptions.data.monthly'),
          value: 'monthlyAmount',
          filterable: false,
          align: 'end',
        },
        { text: this.$t('page.admin-tenants.data.active'), value: 'tenantActive', filterable: false, phone: 'hidden' },
        {
          text: '',
          value: 'data-table-expand',
          sortable: false,
          filterable: false,
          align: 'end',
          width: '40px',
        },
      ],
    };
  },
  computed: {
    ...mapGetters('billing', { billingRows: 'tenants', billingPending: 'pending' }),
    ...mapGetters('tenants', { lifecycleTenants: 'tenants', lifecyclePending: 'pending' }),

    pending() {
      return this.billingPending || this.lifecyclePending;
    },

    isPhone() {
      return viewport.size === 'phone';
    },

    // The account as it is now (a refresh replaces the rows).
    detailsTenant() {
      return this.detailsId ? this.rows.find((row) => row.tenantId === this.detailsId) || null : null;
    },

    detailsOpen: {
      get() {
        return !!this.detailsTenant;
      },
      set(open) {
        if (!open) this.detailsId = null;
      },
    },

    /** Users by tenant id, from the tenants store. */
    usersByTenant() {
      const byId = new Map();
      for (const tenant of this.lifecycleTenants) byId.set(tenant.id, tenant.users || []);
      return byId;
    },

    /** Billing rows, plus tenants the billing view did not return (all of them when it fails). */
    rows() {
      const billed = new Set(this.billingRows.map((r) => r.tenantId));
      const lifecycleOnly = this.lifecycleTenants
        .filter((t) => !billed.has(t.id))
        .map((t) => ({
          tenantId: t.id,
          tenantName: t.name,
          tenantActive: t.active,
          subscriptionType: t.subscriptionType,
          trialStartDate: t.trialStartDate,
          trialEndDate: t.trialEndDate,
          billingUnavailable: true,
        }));
      return lifecycleOnly.length ? [...this.billingRows, ...lifecycleOnly] : this.billingRows;
    },

    billingUnavailableCount() {
      return this.rows.filter((r) => r.billingUnavailable).length;
    },

    /** Rows the status chip lets through; everything else derives from this. */
    baseTenants() {
      if (!this.statusValue) return this.rows;
      const active = this.statusValue === TENANT_STATUS.ACTIVE;
      return this.rows.filter((t) => t.tenantActive === active);
    },

    statusItems() {
      return TENANT_STATUS_VALUES.map((value) => ({
        text: this.$t(`page.admin-tenants.data.${value}`),
        value,
      }));
    },

    /** Summary counts, from the same set the table shows. */
    counts() {
      const t = this.baseTenants;
      return {
        total: t.length,
        linked: t.filter((x) => x.linked).length,
        unlinked: t.filter((x) => !x.linked && !x.billingUnavailable).length,
        needsAttention: t.filter((x) => x.needsAttention).length,
        unbilledPastTrial: t.filter((x) => x.unbilledPastTrial).length,
        paymentIssues: t.filter((x) => x.paymentIssue).length,
        activeWithoutSubscription: t.filter((x) => x.activeWithoutSubscription).length,
        biUnbilled: t.filter((x) => x.biState === 'unbilled').length,
        biPayingNoAccess: t.filter((x) => x.biState === 'paying-no-access').length,
      };
    },

    totalLabel() {
      if (!this.statusValue) return this.$t('page.admin-subscriptions.summary.tenants');
      return this.statusValue === TENANT_STATUS.ACTIVE
        ? this.$t('page.admin-subscriptions.summary.active-tenants')
        : this.$t('page.admin-subscriptions.summary.inactive-tenants');
    },

    /** `biPayingNoAccess` stays listed at zero: it is the one that must never be non-zero. */
    summaryCards() {
      const c = this.counts;
      return [
        { key: null, value: c.total, label: this.totalLabel, tone: 'neutral' },
        { key: 'linked', value: c.linked, label: this.$t('page.admin-subscriptions.summary.linked'), tone: 'ok' },
        // Neutral: most unlinked tenants are legitimately so.
        {
          key: 'unlinked',
          value: c.unlinked,
          label: this.$t('page.admin-subscriptions.summary.unlinked'),
          tone: 'neutral',
        },
        {
          key: 'needsAttention',
          value: c.needsAttention,
          label: this.$t('page.admin-subscriptions.summary.attention'),
          tone: c.needsAttention > 0 ? 'warning' : 'ok',
        },
        {
          key: 'unbilledPastTrial',
          value: c.unbilledPastTrial,
          label: this.$t('page.admin-subscriptions.summary.unbilled-trial'),
          tone: c.unbilledPastTrial > 0 ? 'warning' : 'ok',
        },
        {
          key: 'paymentIssues',
          value: c.paymentIssues,
          label: this.$t('page.admin-subscriptions.summary.payment-issues'),
          tone: c.paymentIssues > 0 ? 'critical' : 'ok',
        },
        {
          key: 'activeWithoutSubscription',
          value: c.activeWithoutSubscription,
          label: this.$t('page.admin-subscriptions.summary.active-no-subscription'),
          tone: c.activeWithoutSubscription > 0 ? 'warning' : 'ok',
        },
        {
          key: 'biUnbilled',
          value: c.biUnbilled,
          label: this.$t('page.admin-subscriptions.summary.bi-unbilled'),
          tone: c.biUnbilled > 0 ? 'warning' : 'ok',
        },
        {
          key: 'biPayingNoAccess',
          value: c.biPayingNoAccess,
          label: this.$t('page.admin-subscriptions.summary.bi-paying-no-access'),
          tone: c.biPayingNoAccess > 0 ? 'critical' : 'ok',
        },
      ];
    },

    /** Card filter, then text search — applied here so the count line matches the table. */
    filteredTenants() {
      const term = this.search.trim().toLowerCase();
      const searched = term
        ? this.baseTenants.filter((t) => (t.tenantName || '').toLowerCase().includes(term))
        : this.baseTenants;

      if (!this.activeFilter) return searched;

      const predicates = {
        linked: (t) => t.linked,
        unlinked: (t) => !t.linked && !t.billingUnavailable,
        needsAttention: (t) => t.needsAttention,
        unbilledPastTrial: (t) => t.unbilledPastTrial,
        paymentIssues: (t) => t.paymentIssue,
        activeWithoutSubscription: (t) => t.activeWithoutSubscription,
        biUnbilled: (t) => t.biState === 'unbilled',
        biPayingNoAccess: (t) => t.biState === 'paying-no-access',
      };

      const predicate = predicates[this.activeFilter];
      return predicate ? searched.filter(predicate) : searched;
    },
  },
  methods: {
    ...mapActions('billing', ['getBillingTenants', 'getUnlinkedCustomers', 'unlinkCustomer', 'resync', 'sync']),
    ...mapActions('tenants', ['getTenants']),

    getBiStateStyle,
    getCardExpiryStyle,
    getSubscriptionStatusStyle,
    getSubscriptionTagStyle,
    getTrialStateStyle,

    /** Tier label, or the raw code when the locale does not know it. */
    planLabel(code) {
      const key = `subscriptions.${code}`;
      return this.$te(key) ? this.$t(key) : code;
    },

    /** The view carries the enum as stored (PREMIUM); styles and labels are keyed lowercase. */
    legacyType(item) {
      return (item.subscriptionType || '').toLowerCase();
    },

    legacyLabel(item) {
      const key = `subscriptions.${this.legacyType(item)}`;
      return this.$te(key) ? this.$t(key) : item.subscriptionType;
    },

    /** Days left on a running trial, or null. */
    trialCountdown(item) {
      if (item.trialState !== 'active') return null;
      // Aloha's date when set; Stripe's when the trial came from a payment link.
      const end = item.trialEndDate || item.stripeTrialEnd;
      if (!end) return null;
      const left = getDaysBeforeClosing(end);
      if (!left) return null;
      return { text: getDhm(left).split(' ')[0], tone: this.getColorClass(left) };
    },

    intervalLabel(interval) {
      if (!interval) return '';
      return this.$t(`page.admin-subscriptions.interval.${interval}`);
    },

    formatAmount(monthly) {
      return `${monthly.toFixed(2)} $`;
    },

    onStatusFilterChange(status) {
      this.statusValue = status ? status : '';
    },

    toggleFilter(key) {
      this.activeFilter = this.activeFilter === key ? null : key;
    },

    isExpanded(item) {
      return this.expanded.some((row) => row && item && row.tenantId === item.tenantId);
    },

    // A row opens under itself on larger screens, in a sheet on phones.
    onRowClick(item) {
      if (this.isPhone) this.detailsId = item.tenantId;
      else this.toggleExpand(item);
    },

    // As in every sheet, a choice that opens a dialog closes it first.
    fromSheet(action) {
      const item = this.detailsTenant;
      this.detailsId = null;
      action(item);
    },

    toggleExpand(item) {
      this.expanded = this.isExpanded(item)
        ? this.expanded.filter((row) => row && row.tenantId !== item.tenantId)
        : [...this.expanded, item];
    },

    refresh() {
      // A sync reloads the lists itself. Guarded here too: BaseRoundButton's `disabled` sits on the
      // inner button while `@click.native` fires on its wrapper.
      if (this.syncing) return Promise.resolve();
      this.expanded = [];
      this.refreshing = true;
      return Promise.all([this.getTenants(), this.getBillingTenants(), this.getUnlinkedCustomers()]).finally(() => {
        this.refreshing = false;
      });
    },

    onRefreshClick() {
      if (this.refreshing) return Promise.resolve();
      return this.refresh();
    },

    /** Runs the nightly job now. The store action reloads the lists on the way back. */
    doSync() {
      // Same wrapper caveat: ignore a second click while one runs.
      if (this.syncing) return Promise.resolve();
      this.syncing = true;
      // About half a minute: say so now; the progress toast stays until the outcome replaces it.
      const progress = this.$toast.info(this.$t('page.admin-subscriptions.sync-started'), { timeout: false });
      return this.sync()
        .then((result) => {
          this.$toast.success(this.$t('page.admin-subscriptions.sync-result', result));
        })
        .catch(() => {
          this.$toast.error(this.$t('page.admin-subscriptions.sync-failed'));
        })
        .finally(() => {
          progress.close();
          this.syncing = false;
        });
    },

    // ---- lifecycle (from Comptes) ----

    newTenantClick() {
      this.$refs.newTenantDialog.openDialog();
    },

    /** The confirm dialog expects the lifecycle shape (id, name, active). */
    askToggleActive(item) {
      this.$refs.updateTenantDialog.openDialog({
        id: item.tenantId,
        name: item.tenantName,
        active: item.tenantActive,
      });
    },

    externalEmailsForTenant(item) {
      return (this.usersByTenant.get(item.tenantId) || [])
        .map((u) => (u && typeof u === 'object' ? u.email : u))
        .filter((email) => email && !String(email).toLowerCase().endsWith('@aloha.app'));
    },

    copyTenantEmails(item) {
      const emails = this.externalEmailsForTenant(item);
      if (!emails.length) return;
      copyTextToClipboard(
        emails.join('; '),
        () => this.$toast.success(this.$t('page.analysis.text-selection.copy.success')),
        () => this.$toast.error(this.$t('page.analysis.text-selection.copy.error')),
      );
    },

    // ---- billing actions (from Abonnements) ----

    openLinkDialog(item) {
      this.$refs.linkDialog.openDialog(item);
    },

    openPaymentLinkDialog(item) {
      this.$refs.paymentLinkDialog.openDialog(item);
    },

    doResync(item) {
      // Guarded here too: a click on the icon reaches @click.native even while the button is disabled.
      if (this.busyRows[item.tenantId]) return Promise.resolve();
      this.$set(this.busyRows, item.tenantId, 'resync');
      return this.resync(item.tenantId)
        .then(() => {
          this.$toast.success(this.$t('page.admin-subscriptions.resync-done', { name: item.tenantName }));
        })
        .catch(() => {
          this.$toast.error(this.$t('page.admin-subscriptions.resync-failed'));
        })
        .finally(() => {
          this.$delete(this.busyRows, item.tenantId);
        });
    },

    /** Confirmed first: unlinking changes what the tenant sees on their billing page. */
    doUnlink(item) {
      if (this.busyRows[item.tenantId]) return;
      this.unlinkTarget = item;
      this.$refs.unlinkDialog.openDialog();
    },

    executeUnlink() {
      const item = this.unlinkTarget;
      if (!item || this.busyRows[item.tenantId]) return Promise.resolve();
      this.$set(this.busyRows, item.tenantId, 'unlink');
      return this.unlinkCustomer(item.tenantId)
        .then(() => {
          this.$toast.success(this.$t('page.admin-subscriptions.unlink-done', { name: item.tenantName }));
        })
        .catch(() => {
          this.$toast.error(this.$t('page.admin-subscriptions.unlink-failed'));
        })
        .finally(() => {
          this.$delete(this.busyRows, item.tenantId);
          this.unlinkTarget = null;
        });
    },
  },
  created() {
    this.refresh();
  },
};
</script>

<style lang="scss">
.page-tenants {
  &__billing-unavailable {
    margin-bottom: 16px;
  }

  &__content {
    display: flex;
    flex: auto;
    flex-direction: column;
    padding: 32px;
  }

  &__summary {
    display: flex;
    flex-wrap: wrap;
    gap: 12px;
    margin-bottom: 24px;

    // Phones: on one line that scrolls sideways, to the screen's edges.
    @include screen-below('tablet') {
      flex-wrap: nowrap;
      margin: 0 (-$phone-page-gutter) $space-md;
      padding: 2px $phone-page-gutter 8px;
      overflow-x: auto;
      scrollbar-width: none;

      &::-webkit-scrollbar {
        display: none;
      }

      .page-tenants__summary-card {
        flex: none;
        min-width: 120px;
      }
    }
  }

  // Phones: the white panel under the header (the lists' search and filters).
  &__phone-panel {
    @include phone-filter-panel {
      position: relative;
      z-index: 2;
      margin-top: -$space-xl;
      margin-bottom: $space-md;
      background: $color-white;
      box-shadow: $shadow-dropdown;
    }
  }

  &__sheet-active {
    display: flex;
    align-items: center;
    justify-content: space-between;
    min-height: 48px;
    margin-bottom: $space-2;
    border-bottom: 1px solid $color-border;
    font-size: 15px;
  }

  &__summary-card {
    display: flex;
    flex-direction: column;
    gap: 2px;
    min-width: 132px;
    padding: 12px 16px;
    background: $color-white;
    border: 1px solid transparent;
    border-radius: $border-radius-root;
    box-shadow: $shadow-default;
    cursor: pointer;
    text-align: left;

    &:hover {
      border-color: $color-neutral-pale;
    }

    &.is-active {
      border-color: $color-normal;
    }

    &.tone-warning .page-tenants__summary-value {
      color: #9c5a0b;
    }

    &.tone-critical .page-tenants__summary-value {
      color: #9b2a2a;
    }

    &.tone-ok .page-tenants__summary-value {
      color: #2c6544;
    }
  }

  &__summary-value {
    font-size: 24px;
    font-weight: 600;
    line-height: 1.1;
  }

  &__summary-label {
    font-size: 12px;
    color: $color-normal;
    opacity: 0.7;
  }

  &__search-area {
    display: flex;
    flex: auto;
    flex-direction: row;
    flex-wrap: nowrap;
    align-items: center;
    gap: 8px;
    padding-bottom: 8px;

    // Phones: the filter and the actions go under the search.
    @include screen-below('tablet') {
      flex-wrap: wrap;
    }

    // Hug the chip instead of the old full-width VSelect's fixed 272px box.
    &--filter-item {
      display: flex;
      align-items: center;
    }
  }

  &__search-bar {
    width: 420px;
  }

  &__muted {
    opacity: 0.45;
  }

  &__inactive {
    text-decoration: line-through;
    opacity: 0.6;
  }

  &__interval {
    font-size: 12px;
    opacity: 0.6;
  }

  &__countdown {
    margin-left: 6px;
    font-size: 12px;
  }

  // Untagged price: a signal, not a display gap.
  &__untagged {
    font-style: italic;
    border-bottom: 1px dashed #9c5a0b;
    cursor: help;
  }

  &__drift {
    color: #9c5a0b;
    margin-left: 6px;
    vertical-align: middle;
  }

  &__active {
    display: inline-flex;
  }

  &__table {
    .v-data-table.theme--light tbody td {
      color: $color-normal;
    }
  }

  .v-data-table__progress {
    .base-loading-bar {
      position: absolute;
      top: 48px;
    }
  }

  &__data-table {
    .v-data-table__expanded__content {
      box-shadow: none;
    }
  }

  &__expanded-cell {
    background: $color-neutral-pale;
    // Not clickable, except its controls; `auto` keeps the text cursor over the facts.
    cursor: auto;

    a,
    button {
      cursor: pointer;
    }
  }

  &__expand-btn {
    vertical-align: middle;
  }
}

// Rows expand on click (see @click:row); the expanded content row itself is not clickable.
.page-tenants__data-table tbody > tr:not(.v-data-table__expanded__content) {
  cursor: pointer;
}
</style>
